import { useCallback, useEffect, useRef, useState } from 'react';

const FILES_DATA_TRANSFER_TYPE = 'Files';

const hasFileType = (types) => {
  if (!types) return false;
  if (typeof types.includes === 'function') return types.includes(FILES_DATA_TRANSFER_TYPE);

  for (let i = 0; i < types.length; i += 1) {
    if (types[i] === FILES_DATA_TRANSFER_TYPE) return true;
  }

  return false;
};

const hasFileTransfer = (dataTransfer) => {
  if (!dataTransfer) return false;
  if (dataTransfer.files && dataTransfer.files.length > 0) return true;
  return hasFileType(dataTransfer.types);
};

const getTransferFiles = (dataTransfer) => {
  if (!dataTransfer || !dataTransfer.files) return [];
  return Array.from(dataTransfer.files).filter((file) => file && file.name);
};

const useReportFileDrop = ({ disabled = false, onDrop }) => {
  const dragCounter = useRef(0);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  const resetDragState = useCallback(() => {
    dragCounter.current = 0;
    setIsDraggingFiles(false);
  }, []);

  useEffect(() => {
    if (disabled) {
      resetDragState();
    }
  }, [disabled, resetDragState]);

  useEffect(() => {
    const handleWindowDragLeave = (event) => {
      const outsideWindow =
        event.clientX <= 0 ||
        event.clientY <= 0 ||
        event.clientX >= window.innerWidth ||
        event.clientY >= window.innerHeight;

      if (outsideWindow) resetDragState();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) resetDragState();
    };

    window.addEventListener('drop', resetDragState);
    window.addEventListener('dragend', resetDragState);
    window.addEventListener('blur', resetDragState);
    window.addEventListener('dragleave', handleWindowDragLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('drop', resetDragState);
      window.removeEventListener('dragend', resetDragState);
      window.removeEventListener('blur', resetDragState);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [resetDragState]);

  const handleDragEnter = useCallback((event) => {
    if (disabled || !hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    dragCounter.current += 1;
    setIsDraggingFiles(true);
  }, [disabled]);

  const handleDragOver = useCallback((event) => {
    if (disabled || !hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, [disabled]);

  const handleDragLeave = useCallback((event) => {
    if (disabled || !hasFileTransfer(event.dataTransfer)) return;
    event.preventDefault();
    dragCounter.current = Math.max(dragCounter.current - 1, 0);
    if (dragCounter.current === 0) setIsDraggingFiles(false);
  }, [disabled]);

  const handleDrop = useCallback((event) => {
    if (disabled || !hasFileTransfer(event.dataTransfer)) return;

    event.preventDefault();
    event.stopPropagation();
    resetDragState();

    const files = getTransferFiles(event.dataTransfer);
    if (files.length === 0) return;

    onDrop(files);

    if (typeof event.dataTransfer.clearData === 'function') {
      event.dataTransfer.clearData();
    }
  }, [disabled, onDrop, resetDragState]);

  return {
    isDraggingFiles,
    dropHandlers: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop
    }
  };
};

export default useReportFileDrop;
